import { FC } from 'react'
import { IKlantGroepModel } from 'shared/models/IKlantGroepModel'
import { IOrderType } from 'shared/models/IOrderType'
import { IProductType } from 'shared/models/IProductType'
import { getDaysDisplay } from 'shared/service/helpers/DateUtilities'
import { getOrderTypesDisplay } from 'shared/service/helpers/OrdertypeUtilities'

interface IProps {
  productTypes: { productType: IProductType; selected: boolean }[]
  possibleOrderTypes: IOrderType[]
  klantGroepen: IKlantGroepModel[]
  showDetails: (
    data: IKlantGroepModel,
    modifyingExistingRecord: boolean
  ) => void
}

const getProductTypeDisplayString = (
  productTypeString: string,
  productTypes: { productType: IProductType; selected: boolean }[]
) => {
  if (productTypeString === 'A') {
    return 'Alle'
  }

  const productType = productTypes.find(
    x => x.productType.value === productTypeString
  )?.productType?.name

  if (!productType) {
    return ''
  }

  return productType
}

const getEsdDisplay = (klantGroep: IKlantGroepModel): string => {
  if (klantGroep.ordertijdIndicatie === 'J') {
    return 'Ordertijd'
  }

  const shipmentDaysSuffix = getShipmentDaysSuffix(
    klantGroep.shipmentDaysBeforeDelivery
  )

  return shipmentDaysSuffix
    ? `${klantGroep.vrijgaveTijdstip}${shipmentDaysSuffix}`
    : klantGroep.vrijgaveTijdstip
}

const getShipmentDaysSuffix = (shipmentDaysBeforeDelivery: number): string => {
  if (shipmentDaysBeforeDelivery > 0) {
    return ` - ${shipmentDaysBeforeDelivery}dg`
  }

  return ''
}

const getLsdDisplay = (klantGroep: IKlantGroepModel): string => {
  if (klantGroep.aflevertijd) {
    return klantGroep.aflevertijd
  } else if (klantGroep.doorlooptijdInMinuten >= 0) {
    return `${klantGroep.doorlooptijdInMinuten} na ESD`
  } else if (klantGroep.lsdLeadTimeBeforeLDD >= 0) {
    return `${klantGroep.lsdLeadTimeBeforeLDD} voor LDD`
  }

  return '-'
}

const getEddWorkDaysSuffix = (eddWorkDaysAfterLSD: number): string => {
  if (eddWorkDaysAfterLSD > 0) {
    return ` + ${eddWorkDaysAfterLSD}dg`
  }

  return ''
}

const getEddDisplay = (klantGroep: IKlantGroepModel): string => {
  if (klantGroep.edd) {
    return `${klantGroep.edd}${getEddWorkDaysSuffix(
      klantGroep.eddWorkDaysAfterLSD
    )}`
  } else if (klantGroep.eddLeadTimeAfterESD >= 0) {
    return `${klantGroep.eddLeadTimeAfterESD} na ESD`
  }

  return '-'
}

const getLddDisplay = (klantGroep: IKlantGroepModel): string => {
  if (klantGroep.ldd) {
    return `${klantGroep.ldd}${getEddWorkDaysSuffix(
      klantGroep.eddWorkDaysAfterLSD
    )}`
  } else if (klantGroep.lddLeadTimeAfterEDD >= 0) {
    return `${klantGroep.lddLeadTimeAfterEDD} na EDD`
  } else if (klantGroep.lddLeadTimeAfterLSD >= 0) {
    return `${klantGroep.lddLeadTimeAfterLSD} na LSD`
  }

  return '-'
}

const getFptDisplay = (klantGroep: IKlantGroepModel): string => {
  if (klantGroep.uitersteInkoopTijdstipIndicatie) {
    return klantGroep.uitersteInkoopTijdstip
  }

  return '-'
}

const getOverflowDisplay = (
  klantGroep: IKlantGroepModel,
  allKlantGroepen: IKlantGroepModel[]
): string => {
  const overflowTimeslot = allKlantGroepen.find(
    x => x.klantGroepId === klantGroep.overflowFPTId
  )

  console.log(
    `${overflowTimeslot} with id ${klantGroep.overflowFPTId} for timeslot ${
      klantGroep.klantGroepCode
    }: ${!overflowTimeslot} ${!klantGroep.overflowFPTId}`
  )

  if (
    !klantGroep.uitersteInkoopTijdstipIndicatie ||
    !overflowTimeslot ||
    !klantGroep.overflowFPTId
  ) {
    return '-'
  }

  return `${overflowTimeslot.klantGroepCode} - ${
    overflowTimeslot?.klantGroepOmschrijving || ''
  }`
}

const getTimeslotDisplay = (klantGroep: IKlantGroepModel): string => {
  if (klantGroep.tijdvakBeginTijdstip && klantGroep.tijdvakEindTijdstip) {
    return `${klantGroep.tijdvakBeginTijdstip} - ${klantGroep.tijdvakEindTijdstip}`
  }

  return '-'
}

const getVasCodeDisplay = (klantGroep: IKlantGroepModel): string => {
  return klantGroep.vasCode?.code || '-'
}

const getCostsDisplay = (klantGroep: IKlantGroepModel): string => {
  if (klantGroep.tijdvakKosten != 0) {
    return `€${klantGroep.tijdvakKosten}`
  }

  return '-'
}

const getDefaultDisplay = (klantGroep: IKlantGroepModel): string => {
  if (klantGroep.defaultIndicatie === 'J') {
    return 'Ja'
  }

  return 'Nee'
}

const OverviewTable: FC<IProps> = ({
  productTypes,
  possibleOrderTypes,
  klantGroepen,
  showDetails,
}) => {
  const renderRows = (data: IKlantGroepModel[]) => {
    return data.map(x => {
      const rowClass = x.pilotIndicatie ? 'tddataPilotRow' : '-'
      return (
        <tr
          key={x.klantGroepId}
          onClick={() => showDetails(x, true)}
          className={rowClass}
        >
          <td className='tddata'>{x.vestigingCode}</td>
          <td className='tddata'>{x.klantGroepCode}</td>
          <td className='tddata wide-column40'>{x.klantGroepOmschrijving}</td>
          <td className='tddata'>
            {x.orderType != null
              ? getOrderTypesDisplay(x.orderType, possibleOrderTypes)
              : '-'}
          </td>
          <td className='tddata'>
            {x.dagen != null ? getDaysDisplay(x.dagen) : '-'}
          </td>
          <td>{getProductTypeDisplayString(x.productType, productTypes)}</td>
          <td>{getEsdDisplay(x)}</td>
          <td className='tddata'>{getLsdDisplay(x)}</td>
          <td className='tddata'>{getEddDisplay(x)}</td>
          <td className='tddata'>{getLddDisplay(x)}</td>
          <td className='tddata'>{getFptDisplay(x)}</td>
          <td className='tddata ellipsis'>{getOverflowDisplay(x, data)}</td>
          <td className='tddata'>{getTimeslotDisplay(x)}</td>
          <td className='tddata'>{getVasCodeDisplay(x)}</td>
          <td className='tddata'>{getCostsDisplay(x)}</td>
          <td className='tddata'>{getDefaultDisplay(x)}</td>
        </tr>
      )
    })
  }

  return (
    <table className='selectie table-hover'>
      <tbody>
        <tr className='tablesubheader'>
          <th>
            <label>Vestiging</label>
          </th>
          <th>
            <label>Code</label>
          </th>
          <th>
            <label>Omschrijving</label>
          </th>
          <th>
            <label>Ordertype</label>
          </th>
          <th>
            <label>Dagen</label>
          </th>
          <th>
            <label>Product</label>
          </th>
          <th>
            <label
              htmlFor='lab_esdtooltip'
              data-toggle='tooltip'
              data-placement='top'
              title='Early Ship Datetime'
            >
              ESD
            </label>
          </th>
          <th>
            <label
              htmlFor='lab_lsdtooltip'
              data-toggle='tooltip'
              data-placement='top'
              title='Latest Shipment Datetime'
            >
              LSD
            </label>
          </th>
          <th className='tddata'>
            <label
              htmlFor='lab_eddtooltip'
              data-toggle='tooltip'
              data-placement='top'
              title='Early Delivery Datetime'
            >
              EDD
            </label>
          </th>
          <th>
            <label
              htmlFor='lab_lddtooltip'
              data-toggle='tooltip'
              data-placement='top'
              title='Latest Delivery Datetime'
            >
              LDD
            </label>
          </th>
          <th>
            <label
              htmlFor='lab_fpttooltip'
              data-toggle='tooltip'
              data-placement='top'
              title='Final Purchasing Time'
            >
              FPT
            </label>
          </th>
          <th>
            <label
              htmlFor='lab_overflowtooltip'
              data-toggle='tooltip'
              data-placement='top'
              title='Overflow Time Slot'
            >
              Overloop
            </label>
          </th>
          <th>
            <label>Begin/Eindtijd</label>
          </th>
          <th>
            <label
              htmlFor='lab_vastooltip'
              data-toggle='tooltip'
              data-placement='top'
              title='Value Added Service Code'
            >
              VAS-code
            </label>
          </th>
          <th>
            <label>Kosten</label>
          </th>
          <th>
            <label>Default</label>
          </th>
        </tr>
        {renderRows(klantGroepen)}
      </tbody>
    </table>
  )
}

export default OverviewTable
